import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  calculateSLAStatus,
  shouldShowSLACountdown,
  SLACalculation,
} from '../../utils/slaUtils';

interface SLACountdownProps {
  slaDeadline: string | Date;
  complaintStatus: string;
  variant?: 'default' | 'compact' | 'detailed';
  showIcon?: boolean;
  className?: string;
}

/**
 * SLACountdown - Real-time countdown timer for complaint SLA deadlines
 *
 * Features:
 * - Updates every second like a chronometer (real-time)
 * - Displays time in HH:MM:SS format
 * - Color-coded based on time remaining (green > 6h, yellow 2-6h, red < 2h, overdue)
 * - Multi-language support (EN, AR, FR)
 * - Stops updating when complaint is resolved/closed
 * - Shows "Completed within SLA" for finished complaints
 *
 * @param slaDeadline - ISO 8601 date string or Date object representing the SLA deadline
 * @param complaintStatus - Current status of the complaint
 * @param variant - Display variant: 'default' (chip), 'compact' (minimal), 'detailed' (with icon and description)
 * @param showIcon - Whether to show status icon (default: true)
 * @param className - Additional CSS classes
 */
const SLACountdown: React.FC<SLACountdownProps> = ({
  slaDeadline,
  complaintStatus,
  variant = 'default',
  showIcon = true,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // State to hold the current SLA calculation
  const [slaInfo, setSlaInfo] = useState<SLACalculation>(() =>
    calculateSLAStatus(slaDeadline, complaintStatus)
  );

  // Update SLA info every second (real-time chronometer)
  useEffect(() => {
    // Only set up interval if complaint is not completed
    if (!shouldShowSLACountdown(complaintStatus)) {
      return;
    }

    // Update immediately
    setSlaInfo(calculateSLAStatus(slaDeadline, complaintStatus));

    // Set up interval to update every second for real-time chronometer
    const intervalId = setInterval(() => {
      setSlaInfo(calculateSLAStatus(slaDeadline, complaintStatus));
    }, 1000); // 1000ms = 1 second

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [slaDeadline, complaintStatus]);

  // Get the appropriate icon based on status
  const getStatusIcon = () => {
    switch (slaInfo.status) {
      case 'safe':
        return <CheckCircleIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      case 'critical':
        return <ErrorIcon fontSize="small" />;
      case 'overdue':
        return <CancelIcon fontSize="small" />;
      default:
        return <ScheduleIcon fontSize="small" />;
    }
  };

  // Get tooltip text based on status
  const getTooltipText = () => {
    const lang = i18n.language || 'en';

    if (complaintStatus === 'resolved' || complaintStatus === 'closed' || complaintStatus === 'closed_by_admin') {
      if (lang === 'ar') {
        return complaintStatus === 'closed_by_admin'
          ? 'تم إغلاق الشكوى من قبل الإدارة'
          : 'تم إنجاز الشكوى ضمن الموعد المحدد';
      } else if (lang === 'fr') {
        return complaintStatus === 'closed_by_admin'
          ? 'Plainte fermée par l\'administrateur'
          : 'Plainte résolue dans le délai imparti';
      } else {
        return complaintStatus === 'closed_by_admin'
          ? 'Complaint closed by admin'
          : 'Complaint completed within the deadline';
      }
    }

    switch (slaInfo.status) {
      case 'safe':
        if (lang === 'ar') {
          return 'وقت كافٍ متبقي';
        } else if (lang === 'fr') {
          return 'Temps suffisant restant';
        } else {
          return 'Sufficient time remaining';
        }
      case 'warning':
        if (lang === 'ar') {
          return 'تحذير: الوقت ينفد';
        } else if (lang === 'fr') {
          return 'Avertissement: Le temps s\'écoule';
        } else {
          return 'Warning: Time is running out';
        }
      case 'critical':
        if (lang === 'ar') {
          return 'حرج: عاجل جداً';
        } else if (lang === 'fr') {
          return 'Critique: Très urgent';
        } else {
          return 'Critical: Very urgent';
        }
      case 'overdue':
        if (lang === 'ar') {
          return 'متأخر: تجاوز الموعد المحدد';
        } else if (lang === 'fr') {
          return 'En retard: Délai dépassé';
        } else {
          return 'Overdue: Deadline passed';
        }
      default:
        return '';
    }
  };

  // Compact variant - minimal display
  if (variant === 'compact') {
    return (
      <Typography
        variant="body2"
        sx={{
          color: slaInfo.color,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
        className={className}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {showIcon && getStatusIcon()}
        {slaInfo.displayText}
      </Typography>
    );
  }

  // Detailed variant - with icon and full description
  if (variant === 'detailed') {
    return (
      <Tooltip title={getTooltipText()} arrow>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.5 },
            padding: { xs: 1.5, sm: 2 },
            backgroundColor: slaInfo.backgroundColor,
            borderRadius: 2,
            border: `2px solid ${slaInfo.color}`,
            minHeight: { xs: '60px', sm: '80px' },
          }}
          className={className}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {showIcon && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: slaInfo.color,
                '& svg': {
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }
              }}
            >
              {getStatusIcon()}
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: slaInfo.color,
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                opacity: 0.9,
                display: 'block',
              }}
            >
              {t('complaintDetail.slaTimer')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: slaInfo.color,
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                lineHeight: 1.2,
                wordBreak: 'break-word',
              }}
            >
              {slaInfo.displayText}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    );
  }

  // Default variant - chip display
  return (
    <Tooltip title={getTooltipText()} arrow>
      <Chip
        icon={showIcon ? getStatusIcon() : undefined}
        label={slaInfo.displayText}
        sx={{
          backgroundColor: slaInfo.backgroundColor,
          color: slaInfo.color,
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '4px 8px',
          height: 'auto',
          '& .MuiChip-icon': {
            color: slaInfo.color,
          },
          ...(slaInfo.status === 'overdue' && {
            animation: 'pulse 2s infinite',
          }),
          '@keyframes pulse': {
            '0%, 100%': {
              opacity: 1,
            },
            '50%': {
              opacity: 0.8,
            },
          },
        }}
        className={className}
      />
    </Tooltip>
  );
};

export default SLACountdown;
